import type { LucideIcon } from 'lucide-react';
import {
	LayoutDashboard,
	ShoppingCart,
	Users,
	BarChart3,
	Package,
	Sliders,
	Coins,
	Feather,
	Megaphone,
	Award,
	Gift,
	Mail,
	FileText,
	Puzzle,
	Settings,
	ArrowLeftRight,
	CalendarCheck,
	Undo2
} from 'lucide-react';
/**
 * A per-site module switch that decides whether a page exists in this shop at all — a different question
 * from `requiredScope`, which decides whether this administrator may open it. Resolved by
 * `~/hooks/use-menu-features`, which both navigation surfaces read. `appointments` is the optional Appointments
 * module (Bookings + CMS Events, BW-8453), `contests` the Contests module and `wishlistTemplates` the WishlistTemplates
 * module (ready-made wishlists, BW-8477), all switched on by the API's `BizWebSettings:SpecialModules`.
 */
export type MenuFeature = 'returns' | 'appointments' | 'contests' | 'wishlistTemplates';

export interface MenuChild {
	id: string;
	labelKey: string;
	path: string;
	requiredScope?: string;
	/** Per-site module switch gating this child (see `isMenuEntryVisible`). */
	requiredFeature?: MenuFeature;
	/** Optional live badge rendered next to the label (see sidebar-sub-item.tsx). */
	badge?: 'aiPendingCount' | 'pendingReviewsCount';
}

export interface MenuItem {
	id: string;
	labelKey: string;
	icon: LucideIcon;
	path?: string;
	children?: MenuChild[];
	requiredScope?: string;
	/** Per-site module switch gating this item and all its children (see `isMenuEntryVisible`). */
	requiredFeature?: MenuFeature;
}

export interface MenuSection {
	id: string;
	labelKey: string;
	items: MenuItem[];
}

/**
 * Canonical admin-v2 navigation: 4 sections, 17 top-level items, ~70 children.
 * Labels are i18n keys (resolved + uppercased for section labels at render time).
 *
 * Only the `Λίστα Πολιτικών` child (`/promotions`) and `Dashboard` (`/dashboard`)
 * point at shipped/forthcoming surfaces; every other route resolves to the
 * <ComingSoonPlaceholder/> until that page is migrated. Paths for unmigrated pages
 * are provisional and may be refined when each page lands.
 *
 * Note on label keys: i18next cannot have `menu.orders` be both a string (parent
 * label) and an object (`menu.orders.list`), so children use flat camelCase keys
 * (`menu.ordersList`) — a deliberate deviation from the spec's illustrative dotted
 * form to avoid the resource collision.
 */
export const menu: MenuSection[] = [
	{
		id: 'daily',
		labelKey: 'menu.section.daily',
		items: [
			{ id: 'dashboard', labelKey: 'menu.dashboard', icon: LayoutDashboard, path: '/dashboard' },
			{
				id: 'orders',
				labelKey: 'menu.orders',
				icon: ShoppingCart,
				children: [
					{ id: 'orders-list', labelKey: 'menu.ordersList', path: '/orders' },
					{ id: 'orders-skroutz', labelKey: 'menu.ordersSkroutz', path: '/orders/skroutz' },
					{ id: 'orders-create', labelKey: 'menu.ordersCreate', path: '/orders/new' },
					{ id: 'orders-filtered', labelKey: 'menu.ordersFiltered', path: '/orders/filtered' },
					{ id: 'orders-payment-methods', labelKey: 'menu.ordersPaymentMethods', path: '/orders/payment-methods' },
					{ id: 'orders-shipping-methods', labelKey: 'menu.ordersShippingMethods', path: '/orders/shipping-methods' },
					{ id: 'orders-pickup-points', labelKey: 'menu.ordersPickupPoints', path: '/orders/pickup-points' },
					{ id: 'orders-internal-statuses', labelKey: 'menu.ordersInternalStatuses', path: '/orders/internal-statuses' }
				]
			},
			{
				// Its own section rather than a child of Orders: a return request spans pieces of an order and is
				// worked as a queue of its own, and its configuration is a page in its own right.
				id: 'returns',
				labelKey: 'menu.returns',
				icon: Undo2,
				children: [
					// The queue disappears when `Returns.Enabled` is off, as it does in admin-v1 — a page that can
					// only answer "disabled" is navigation that leads nowhere. The gate sits HERE and not on the
					// parent on purpose: see the settings child below.
					{ id: 'returns-list', labelKey: 'menu.returnsList', path: '/returns', requiredFeature: 'returns' },
					// `Returns.Enabled` is one of the variables edited here, so the page is System-scoped like every
					// other write to the Settings table — see ReturnSettingsController. Deliberately NOT gated on
					// `requiredFeature`: the switch that turns the module on lives on this page, so hiding it while
					// the module is off would leave no way to turn it on. Same split as admin-v1's ReturnsMenu.
					{ id: 'returns-settings', labelKey: 'menu.returnsSettings', path: '/returns/settings', requiredScope: 'System' }
				]
			},
			{
				id: 'customers',
				labelKey: 'menu.customers',
				icon: Users,
				children: [
					{ id: 'customers-list', labelKey: 'menu.customersList', path: '/customers' },
					{ id: 'customers-categories', labelKey: 'menu.customersCategories', path: '/customers/categories' },
					{ id: 'customers-groups', labelKey: 'menu.customersGroups', path: '/customers/groups' },
					{ id: 'customers-tiers', labelKey: 'menu.customersTiers', path: '/customers/tiers' },
					{ id: 'customers-register-requests', labelKey: 'menu.customersRegisterRequests', path: '/customers/register-requests' },
					{ id: 'customers-sales-persons', labelKey: 'menu.customersSalesPersons', path: '/customers/sales-persons' },
					{ id: 'customers-inspectors', labelKey: 'menu.customersInspectors', path: '/customers/inspectors' }
					// Access Rules / Branches / Coupons are per-customer sub-views launched from row actions
					// in the Customers grid (not standalone menu pages).
				]
			},
			{
				id: 'bookings',
				labelKey: 'menu.bookings',
				icon: CalendarCheck,
				requiredScope: 'Bookings',
				requiredFeature: 'appointments',
				children: [
					{ id: 'bookings-list', labelKey: 'menu.bookingsList', path: '/bookings' },
					{ id: 'bookings-types', labelKey: 'menu.bookingsTypes', path: '/bookings/types' },
					{ id: 'bookings-representatives', labelKey: 'menu.bookingsRepresentatives', path: '/bookings/representatives' },
					{ id: 'bookings-reports', labelKey: 'menu.bookingsReports', path: '/bookings/reports' }
				]
			},
			{
				id: 'reports',
				labelKey: 'menu.reports',
				icon: BarChart3,
				children: [
					{ id: 'reports-sales', labelKey: 'menu.reportsSales', path: '/reports/sales' },
					{ id: 'reports-brands', labelKey: 'menu.reportsBrands', path: '/reports/brands' },
					{ id: 'reports-categories', labelKey: 'menu.reportsCategories', path: '/reports/categories' },
					{ id: 'reports-products', labelKey: 'menu.reportsProducts', path: '/reports/products' },
					{ id: 'reports-coupons', labelKey: 'menu.reportsCoupons', path: '/reports/coupons' },
					{ id: 'reports-geographic', labelKey: 'menu.reportsGeographic', path: '/reports/geographic' }
				]
			}
		]
	},
	{
		id: 'catalog',
		labelKey: 'menu.section.catalog',
		items: [
			{
				id: 'products',
				labelKey: 'menu.products',
				icon: Package,
				children: [
					{ id: 'products-list', labelKey: 'menu.productsList', path: '/products' },
					{ id: 'products-categories', labelKey: 'menu.productsCategories', path: '/products/categories' },
					{ id: 'products-manufacturers', labelKey: 'menu.productsManufacturers', path: '/products/manufacturers' },
					{ id: 'products-statuses', labelKey: 'menu.productsStatuses', path: '/products/statuses' },
					{ id: 'products-seasons', labelKey: 'menu.productsSeasons', path: '/products/seasons' },
					{ id: 'products-models', labelKey: 'menu.productsModels', path: '/products/models' },
					{ id: 'products-relations', labelKey: 'menu.productsRelations', path: '/products/relations' },
					{
						id: 'products-reviews',
						labelKey: 'menu.productsReviews',
						path: '/products/reviews',
						requiredScope: 'Catalog',
						badge: 'pendingReviewsCount'
					},
					{ id: 'products-back-in-stock', labelKey: 'menu.productsBackInStock', path: '/products/back-in-stock', requiredScope: 'Catalog' },
					{ id: 'products-filters', labelKey: 'menu.productsFilters', path: '/products/filters' },
					{ id: 'products-warehouses', labelKey: 'menu.productsWarehouses', path: '/products/warehouses' },
					{
						id: 'products-wishlist-templates',
						labelKey: 'menu.productsWishlistTemplates',
						path: '/products/wishlist-templates',
						requiredFeature: 'wishlistTemplates'
					},
					{ id: 'products-filter-templates', labelKey: 'menu.productsFilterTemplates', path: '/products/filter-templates' },
					{ id: 'products-theme-templates', labelKey: 'menu.productsThemeTemplates', path: '/products/theme-templates' },
					{ id: 'products-buy-guides', labelKey: 'menu.productsBuyGuides', path: '/products/buy-guides', requiredScope: 'Catalog' },
					{ id: 'products-bundles', labelKey: 'menu.productsBundles', path: '/products/bundles', requiredScope: 'Catalog' }
				]
			},
			{
				id: 'attributes',
				labelKey: 'menu.attributes',
				icon: Sliders,
				children: [
					{ id: 'attributes-list', labelKey: 'menu.attributesList', path: '/attributes' },
					{ id: 'attributes-categories', labelKey: 'menu.attributesCategories', path: '/attributes/categories' },
					{ id: 'attributes-map', labelKey: 'menu.attributesMap', path: '/attributes/category-map' },
					{ id: 'attributes-dimensions', labelKey: 'menu.attributesDimensions', path: '/attributes/dimensions' },
					{ id: 'attributes-dimension-grouping', labelKey: 'menu.attributesDimensionGrouping', path: '/attributes/dimensions-grouping' },
					{ id: 'attributes-size-guides', labelKey: 'menu.attributesSizeGuides', path: '/attributes/size-guides' }
				]
			},
			{
				id: 'prices',
				labelKey: 'menu.prices',
				icon: Coins,
				children: [
					{ id: 'prices-types', labelKey: 'menu.pricesTypes', path: '/prices/types' },
					{ id: 'prices-tax-categories', labelKey: 'menu.pricesTaxCategories', path: '/prices/tax-categories' },
					{ id: 'prices-tax-rules', labelKey: 'menu.pricesTaxRules', path: '/prices/tax-rules' },
					{ id: 'prices-currency-rates', labelKey: 'menu.pricesCurrencyRates', path: '/prices/currency-rates' }
				]
			},
			{
				id: 'contributors',
				labelKey: 'menu.contributors',
				icon: Feather,
				children: [
					{ id: 'contributors-list', labelKey: 'menu.contributorsList', path: '/contributors' },
					{ id: 'contributors-categories', labelKey: 'menu.contributorsCategories', path: '/contributors/categories' },
					{ id: 'contributors-groups', labelKey: 'menu.contributorsGroups', path: '/contributors/groups' }
				]
			}
		]
	},
	{
		id: 'marketing',
		labelKey: 'menu.section.marketing',
		items: [
			{
				id: 'promotions',
				labelKey: 'menu.promotions',
				icon: Megaphone,
				children: [
					{ id: 'promotions-list', labelKey: 'menu.promotionsList', path: '/promotions' },
					{ id: 'promotions-coupons', labelKey: 'menu.promotionsCoupons', path: '/promotions/coupons' },
					{ id: 'promotions-discount-caps', labelKey: 'menu.promotionsDiscountCaps', path: '/promotions/discount-caps' }
				]
			},
			{
				id: 'loyalty',
				labelKey: 'menu.loyalty',
				icon: Award,
				children: [
					{ id: 'loyalty-overview', labelKey: 'menu.loyaltyOverview', path: '/loyalty' },
					{ id: 'loyalty-products', labelKey: 'menu.loyaltyProducts', path: '/loyalty/products' },
					{ id: 'loyalty-ranges', labelKey: 'menu.loyaltyRanges', path: '/loyalty/ranges' },
					{ id: 'loyalty-customers', labelKey: 'menu.loyaltyCustomers', path: '/loyalty/customers' },
					{ id: 'loyalty-per-entity', labelKey: 'menu.loyaltyPerEntity', path: '/loyalty/per-entity' },
					{ id: 'loyalty-tier-rules', labelKey: 'menu.loyaltyTierRules', path: '/loyalty/tier-rules' }
				]
			},
			{
				id: 'giftcards',
				labelKey: 'menu.giftcards',
				icon: Gift,
				children: [{ id: 'giftcards-orders', labelKey: 'menu.giftcardsOrders', path: '/gift-cards/orders' }]
			},
			{
				id: 'newsletters',
				labelKey: 'menu.newsletters',
				icon: Mail,
				children: [{ id: 'newsletters-subscriptions', labelKey: 'menu.newslettersSubscriptions', path: '/newsletters/subscriptions' }]
			},
			{
				id: 'content',
				labelKey: 'menu.content',
				icon: FileText,
				children: [
					{ id: 'content-pages', labelKey: 'menu.contentPages', path: '/content/pages' },
					{ id: 'content-banners', labelKey: 'menu.contentBanners', path: '/content/banners' },
					{ id: 'content-banner-groups', labelKey: 'menu.contentBannerGroups', path: '/content/banner-groups' },
					{ id: 'content-menus', labelKey: 'menu.contentMenus', path: '/content/menus' },
					{ id: 'content-footer', labelKey: 'menu.contentFooter', path: '/content/footer' },
					{ id: 'content-faqs', labelKey: 'menu.contentFaqs', path: '/content/faqs' },
					{ id: 'content-news', labelKey: 'menu.contentNews', path: '/content/news' },
					{ id: 'content-events', labelKey: 'menu.contentEvents', path: '/content/events', requiredFeature: 'appointments' },
					{ id: 'content-social-media', labelKey: 'menu.contentSocialMedia', path: '/content/social-media' },
					{ id: 'content-logo', labelKey: 'menu.contentLogo', path: '/content/logo' },
					{ id: 'content-custom-message-groups', labelKey: 'menu.contentCustomMessageGroups', path: '/content/custom-message-groups' },
					{ id: 'content-custom-messages', labelKey: 'menu.contentCustomMessages', path: '/content/custom-messages' },
					{ id: 'content-custom-content', labelKey: 'menu.contentCustomContent', path: '/content/custom-content' },
					{ id: 'content-news-groups', labelKey: 'menu.contentNewsGroups', path: '/content/news-groups' },
					{ id: 'content-video-groups', labelKey: 'menu.contentVideoGroups', path: '/content/video-groups' },
					{
						id: 'content-notification-categories',
						labelKey: 'menu.contentNotificationCategories',
						path: '/content/notification-categories'
					},
					{ id: 'content-notifications', labelKey: 'menu.contentNotifications', path: '/content/notifications' },
					{ id: 'content-news-entries-tags', labelKey: 'menu.contentNewsTags', path: '/content/news-entries-tags' },
					{ id: 'content-event-tags', labelKey: 'menu.contentEventTags', path: '/content/event-tags', requiredFeature: 'appointments' },
					{ id: 'content-contact-forms', labelKey: 'menu.contentContactForms', path: '/content/contact-forms' },
					{ id: 'content-push-subscriptions', labelKey: 'menu.contentPushSubscriptions', path: '/content/push-subscriptions' },
					{ id: 'content-file-manager', labelKey: 'menu.contentFileManager', path: '/content/file-manager' },
					{ id: 'content-news-categories', labelKey: 'menu.contentNewsCategories', path: '/content/news-categories' },
					{
						id: 'content-event-categories',
						labelKey: 'menu.contentEventCategories',
						path: '/content/event-categories',
						requiredFeature: 'appointments'
					},
					{
						id: 'content-event-locations',
						labelKey: 'menu.contentEventLocations',
						path: '/content/event-locations',
						requiredFeature: 'appointments'
					},
					{ id: 'content-video-categories', labelKey: 'menu.contentVideoCategories', path: '/content/video-categories' },
					{ id: 'content-videos', labelKey: 'menu.contentVideos', path: '/content/videos' }
				]
			}
		]
	},
	{
		id: 'system',
		labelKey: 'menu.section.system',
		items: [
			{
				id: 'special-modules',
				labelKey: 'menu.specialModules',
				icon: Puzzle,
				requiredScope: 'System',
				children: [
					{ id: 'modules-contests', labelKey: 'menu.modulesContests', path: '/modules/contests', requiredFeature: 'contests' },
					{ id: 'modules-product-tabs', labelKey: 'menu.modulesProductTabs', path: '/modules/product-tabs' }
				]
			},
			{
				id: 'system',
				labelKey: 'menu.system',
				icon: Settings,
				requiredScope: 'System',
				children: [
					{ id: 'system-languages', labelKey: 'menu.systemLanguages', path: '/system/languages' },
					{ id: 'system-sites', labelKey: 'menu.systemSites', path: '/system/sites' },
					{ id: 'system-countries', labelKey: 'menu.systemCountries', path: '/system/countries' },
					{ id: 'system-country-regions', labelKey: 'menu.systemCountryRegions', path: '/system/country-regions' },
					{ id: 'system-cities', labelKey: 'menu.systemCities', path: '/system/cities' },
					{ id: 'system-postal-codes', labelKey: 'menu.systemPostalCodes', path: '/system/postal-codes' },
					{ id: 'system-tax-offices', labelKey: 'menu.systemTaxOffices', path: '/system/tax-offices' },
					{ id: 'system-email-templates', labelKey: 'menu.systemEmailTemplates', path: '/system/email-templates' },
					{ id: 'system-settings', labelKey: 'menu.systemSettings', path: '/system/settings' },
					{ id: 'system-event-subscriptions', labelKey: 'menu.systemEventSubscriptions', path: '/system/event-subscriptions' },
					{ id: 'system-task-schedules', labelKey: 'menu.systemTaskSchedules', path: '/system/task-schedules' },
					{ id: 'system-user-roles', labelKey: 'menu.systemUserRoles', path: '/system/user-roles' },
					{ id: 'system-users', labelKey: 'menu.systemUsers', path: '/system/users' },
					{ id: 'system-api-keys', labelKey: 'menu.systemApiKeys', path: '/system/api-keys' },
					{ id: 'system-mcp-keys', labelKey: 'menu.systemMcpKeys', path: '/system/mcp-keys' },
					{
						id: 'system-ai-pending-actions',
						labelKey: 'menu.systemAiPendingActions',
						path: '/system/ai-pending-actions',
						badge: 'aiPendingCount'
					},
					{ id: 'system-activity-log', labelKey: 'menu.systemActivityLog', path: '/system/activity-log' },
					{ id: 'system-sitemap-pages', labelKey: 'menu.systemSitemapPages', path: '/system/sitemap-pages' },
					{ id: 'system-robots-file', labelKey: 'menu.systemRobotsFile', path: '/system/robots-file' },
					{ id: 'system-url-redirects', labelKey: 'menu.systemUrlRedirects', path: '/system/url-redirects' },
					{ id: 'system-system-log', labelKey: 'menu.systemSystemLog', path: '/system/system-log' },
					{ id: 'system-erp-logs', labelKey: 'menu.systemErpLogs', path: '/system/erp-logs' },
					{ id: 'system-sms-templates', labelKey: 'menu.systemSmsTemplates', path: '/system/sms-templates' },
					{ id: 'system-available-dates', labelKey: 'menu.systemAvailableDates', path: '/system/available-dates' }
				]
			},
			{
				id: 'import-export',
				labelKey: 'menu.importExport',
				icon: ArrowLeftRight,
				requiredScope: 'System',
				children: [
					{ id: 'import-export-import', labelKey: 'menu.importExportImport', path: '/import-export/import' },
					{ id: 'import-export-export', labelKey: 'menu.importExportExport', path: '/import-export/export' }
				]
			}
		]
	}
];

/**
 * How a per-site module switch answers a menu gate.
 *
 * `undefined` is "not answered yet, or the request failed", and both count as **available**. Only an explicit
 * `false` hides. Turning this into `=== true` would hide the section on every slow first paint, and would hide
 * it for five minutes over a dropped connection — removing the page that would have explained the failure.
 * Lives here, next to the gate it feeds, so a second feature switch copies the rule instead of reinventing it.
 */
export function isFeatureAvailable(enabled: boolean | undefined): boolean {
	return enabled !== false;
}

/**
 * Whether one menu entry is visible to this administrator, in this shop. Independent axes, all of which must
 * pass: `requiredScope` asks whether they may open the page; `requiredFeature` (per-site module switch) whether
 * the page exists here at all.
 *
 * Extracted rather than inlined because **every** navigation surface must answer this identically — the
 * sidebar, the command palette, and whatever comes next.
 */
export function isMenuEntryVisible(
	entry: Pick<MenuItem, 'requiredScope' | 'requiredFeature'>,
	hasAccess: (scope: string) => boolean,
	hasFeature: (feature: MenuFeature) => boolean
): boolean {
	return (!entry.requiredScope || hasAccess(entry.requiredScope)) && (!entry.requiredFeature || hasFeature(entry.requiredFeature));
}

/**
 * The menu as this administrator should see it: entries they cannot reach removed, then anything left
 * empty removed with them.
 *
 * The last filter is not cosmetic. `SidebarItem` decides leaf-versus-parent from `children?.length`, so a
 * parent that *had* children and lost them all falls into the leaf branch and renders a link to
 * `item.path` — which a pure parent does not have. Dropping it is also the honest answer: a section that
 * expands to nothing is worse than no section.
 */
export function visibleMenu(hasAccess: (scope: string) => boolean, hasFeature: (feature: MenuFeature) => boolean): MenuSection[] {
	return menu
		.map(section => ({
			...section,
			items: section.items
				.filter(item => isMenuEntryVisible(item, hasAccess, hasFeature))
				.map(item =>
					item.children ? { ...item, children: item.children.filter(child => isMenuEntryVisible(child, hasAccess, hasFeature)) } : item
				)
				.filter(item => !item.children || item.children.length > 0)
		}))
		.filter(section => section.items.length > 0);
}

/** Flat list of every routable path in the menu (used to register routes + placeholder titles). */
export const menuPaths: { path: string; itemId: string; labelKey: string }[] = menu.flatMap(section =>
	section.items.flatMap(item => {
		const own = item.path ? [{ path: item.path, itemId: item.id, labelKey: item.labelKey }] : [];
		const kids = (item.children ?? []).map(child => ({
			path: child.path,
			itemId: child.id,
			labelKey: child.labelKey
		}));
		return [...own, ...kids];
	})
);

/**
 * The single menu path that should be considered active for a given location: the LONGEST menu
 * path that equals or prefixes `pathname`. "Best match wins" so a more specific item (e.g.
 * `/customers/groups`) beats its ancestor (`/customers`) — fixing the double-highlight on nested
 * routes — while detail routes with no own menu entry still resolve to their nearest ancestor.
 * Pure (no React) so callers can use it outside the component lifecycle.
 */
export function getActiveMenuPath(pathname: string): string | undefined {
	let best: string | undefined;
	for (const { path } of menuPaths) {
		if ((pathname === path || pathname.startsWith(path + '/')) && (best === undefined || path.length > best.length)) {
			best = path;
		}
	}
	return best;
}
